import { getAdminToken } from "@/lib/adminToken";
import { useCallback, useEffect, useState, type FormEvent, type ChangeEvent } from "react";
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, Save, Trash2, Users, X } from "lucide-react";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image_url: string;
  bio: string;
  display_order: number;
  is_active: boolean;
}

interface TeamForm {
  name: string;
  role: string;
  image_url: string;
  bio: string;
  display_order: number;
  is_active: boolean;
}

const emptyForm: TeamForm = {
  name: "",
  role: "",
  image_url: "",
  bio: "",
  display_order: 1,
  is_active: true,
};

const tokenHeaders = () => ({
  Authorization: `Bearer ${getAdminToken()}`,
});

export default function AdminTeam() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [form, setForm] = useState<TeamForm>(emptyForm);
  const [uploadedImage, setUploadedImage] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/team?all=1", { headers: tokenHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not load team members.");
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load team members.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, display_order: members.length + 1 });
    setUploadedImage("");
    setError("");
    setNotice("");
    setModalOpen(true);
  };

  const openEdit = (member: TeamMember) => {
    setEditing(member);
    setForm({
      name: member.name,
      role: member.role,
      image_url: member.image_url,
      bio: member.bio || "",
      display_order: member.display_order,
      is_active: member.is_active,
    });
    setUploadedImage("");
    setError("");
    setNotice("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setUploadedImage("");
  };

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file.");
      return;
    }
    if (file.size > 7 * 1024 * 1024) {
      setError("Images must be 7 MB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(typeof reader.result === "string" ? reader.result : "");
      setError("");
    };
    reader.onerror = () => setError("Could not read this image.");
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    const payload = { ...form, image_url: uploadedImage || form.image_url };
    try {
      const response = await fetch(editing ? `/api/team/${editing.id}` : "/api/team", {
        method: editing ? "PUT" : "POST",
        headers: { ...tokenHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save team member.");
      setModalOpen(false);
      setEditing(null);
      setUploadedImage("");
      setNotice(editing ? "Team member updated." : "Team member added.");
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save team member.");
    } finally {
      setSaving(false);
    }
  };

  const moveMember = async (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= members.length) return;
    const first = members[index];
    const second = members[target];
    setSaving(true);
    setError("");
    try {
      const updates = await Promise.all([
        fetch(`/api/team/${first.id}`, {
          method: "PUT",
          headers: { ...tokenHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ ...first, display_order: second.display_order }),
        }),
        fetch(`/api/team/${second.id}`, {
          method: "PUT",
          headers: { ...tokenHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ ...second, display_order: first.display_order }),
        }),
      ]);
      for (const response of updates) {
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Could not update order.");
        }
      }
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update order.");
    } finally {
      setSaving(false);
    }
  };

  const deleteMember = async (id: number) => {
    setError("");
    try {
      const response = await fetch(`/api/team/${id}`, {
        method: "DELETE",
        headers: tokenHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not delete team member.");
      setDeleteId(null);
      setNotice("Team member deleted.");
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete team member.");
    }
  };

  const previewImage = uploadedImage || form.image_url;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="h-0.5 w-8 rounded-full bg-accent" />
            <span className="text-xs font-medium uppercase tracking-wide text-accent">About page</span>
          </div>
          <h1 className="text-2xl font-bold text-primary">Our Team</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage the profiles shown in “Meet Our Experts”.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-accent/20"
        >
          <Plus className="h-4 w-4" /> Add team member
        </button>
      </div>

      {error && <p role="alert" className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">{error}</p>}
      {notice && <p role="status" className="rounded-xl border border-green-100 bg-green-50 p-4 text-sm text-green-700">{notice}</p>}

      {loading ? (
        <div className="flex justify-center rounded-xl border border-neutral-100 bg-white py-16">
          <Loader2 className="h-7 w-7 animate-spin text-accent" />
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border border-neutral-100 bg-white py-16 text-center">
          <Users className="mx-auto mb-3 h-9 w-9 text-neutral-300" />
          <h2 className="font-semibold text-primary">No team members yet</h2>
          <p className="mt-1 text-sm text-neutral-500">Add the people you want to feature on the About page.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white">
          <div className="divide-y divide-neutral-100">
            {members.map((member, index) => (
              <div key={member.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  <img
                    src={member.image_url || "/icon.png"}
                    alt=""
                    className="h-16 w-14 rounded-lg bg-neutral-100 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate font-semibold text-primary">{member.name}</h2>
                      {!member.is_active && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">Hidden</span>}
                    </div>
                    <p className="text-sm text-accent">{member.role}</p>
                    {member.bio && <p className="mt-1 line-clamp-2 text-xs text-neutral-500">{member.bio}</p>}
                    <p className="mt-1 text-xs text-neutral-400">Display order: {member.display_order}</p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button type="button" onClick={() => moveMember(index, -1)} disabled={index === 0 || saving} aria-label={`Move ${member.name} up`} className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => moveMember(index, 1)} disabled={index === members.length - 1 || saving} aria-label={`Move ${member.name} down`} className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" onClick={() => openEdit(member)} aria-label={`Edit ${member.name}`} className="rounded-lg p-2 text-neutral-500 hover:bg-accent/10 hover:text-accent"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setDeleteId(member.id)} aria-label={`Delete ${member.name}`} className="rounded-lg p-2 text-neutral-500 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4" onClick={closeModal}>
          <div className="my-auto w-full max-w-2xl rounded-2xl bg-white p-5 shadow-2xl sm:p-7" onClick={(event) => event.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-primary">{editing ? "Edit team member" : "Add team member"}</h2>
                <p className="mt-1 text-sm text-neutral-500">The public team section follows this display order.</p>
              </div>
              <button type="button" onClick={closeModal} className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-neutral-700">Name
                  <input required maxLength={120} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2.5 font-normal outline-none focus:border-accent" />
                </label>
                <label className="block text-sm font-medium text-neutral-700">Role / title
                  <input required maxLength={120} value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2.5 font-normal outline-none focus:border-accent" />
                </label>
              </div>
              <label className="block text-sm font-medium text-neutral-700">Short bio
                <textarea rows={3} maxLength={2000} value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2.5 font-normal outline-none focus:border-accent" />
              </label>
              <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                <label className="block text-sm font-medium text-neutral-700">Image URL or site path
                  <input value={form.image_url} onChange={(event) => { setForm({ ...form, image_url: event.target.value }); setUploadedImage(""); }} placeholder="/team/photo.jpg or https://…" className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2.5 font-normal outline-none focus:border-accent" />
                </label>
                <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50">
                  <Plus className="h-4 w-4" /> Upload photo
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImage} className="sr-only" />
                </label>
              </div>
              {previewImage && <img src={previewImage} alt="Team member preview" className="h-36 w-28 rounded-xl bg-neutral-100 object-cover" />}
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-neutral-700">Display order
                  <input type="number" min={0} max={10000} required value={form.display_order} onChange={(event) => setForm({ ...form, display_order: Number(event.target.value) })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2.5 font-normal outline-none focus:border-accent" />
                </label>
                <label className="flex items-center gap-3 self-end rounded-xl border border-neutral-200 p-3 text-sm text-neutral-700">
                  <input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} className="h-4 w-4 accent-accent" /> Show on About page
                </label>
              </div>
              {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
              <div className="flex justify-end gap-3 border-t border-neutral-100 pt-4">
                <button type="button" onClick={closeModal} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-600">Cancel</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editing ? "Save changes" : "Add member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDeleteId(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-lg font-semibold text-primary">Delete this team member?</h2>
            <p className="mt-2 text-sm text-neutral-500">Their profile will be removed from the About page.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setDeleteId(null)} className="rounded-xl border border-neutral-200 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={() => deleteMember(deleteId)} className="rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
